import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Map as MapIcon } from "lucide-react";

const REGION_HEX = {
  americas: "#059669",
  europe: "#0284c7",
  north_america: "#7c3aed",
  middle_east: "#d97706",
  asia: "#e11d48",
};

export default function HubMap({ hubs, onPickHub, selectedCode }) {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => {
    if (!containerRef.current || hubs.length === 0) return;

    const build = () => {
      const L = window.L;
      if (!L || !containerRef.current) return;
      if (!mapRef.current) {
        mapRef.current = L.map(containerRef.current, { zoomControl: true, scrollWheelZoom: false });
        L.tileLayer(
          process.env.REACT_APP_MAP_TILE_URL || "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
          {
            attribution: process.env.REACT_APP_MAP_TILE_ATTRIBUTION
              || '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            maxZoom: 10,
          }
        ).addTo(mapRef.current);
      }
      const map = mapRef.current;

      markersRef.current.forEach(m => map.removeLayer(m));
      markersRef.current = [];

      const points = [];
      hubs.forEach(hub => {
        if (!hub.coords) return;
        const color = REGION_HEX[hub.region] || "#0284c7";
        const isSelected = hub.code === selectedCode;
        const icon = L.divIcon({
          className: "",
          html: `<div style="
            background:${color};color:#fff;width:${isSelected ? 42 : 34}px;height:${isSelected ? 42 : 34}px;
            border-radius:50%;display:flex;align-items:center;justify-content:center;
            font-weight:700;font-size:10px;letter-spacing:.5px;cursor:pointer;
            border:3px solid ${isSelected ? "#0f172a" : "rgba(255,255,255,.95)"};
            box-shadow:0 2px 10px rgba(15,23,42,.35);
          ">${hub.code}</div>`,
          iconSize: [34, 34],
          iconAnchor: [17, 17],
        });
        const marker = L.marker(hub.coords, { icon, title: hub.name }).addTo(map);
        marker.bindTooltip(`${hub.name} · ${t("hub.available")}`, { direction: "top", offset: [0, -16] });
        marker.on("click", () => onPickHub(hub.code));
        markersRef.current.push(marker);
        points.push(hub.coords);
      });

      const applyView = () => {
        map.invalidateSize();
        try {
          const bounds = points.length > 0 ? L.latLngBounds(points) : null;
          if (bounds && bounds.isValid()) {
            map.fitBounds(bounds, { padding: [20, 20] });
          } else {
            map.setView([20, 0], 2);
          }
        } catch {
          map.setView([20, 0], 2);
        }
      };
      applyView();
      setTimeout(applyView, 250);
    };

    if (window.L) {
      build();
    } else {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);
      const script = document.createElement("script");
      script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
      script.onload = build;
      document.body.appendChild(script);
    }
  }, [hubs, selectedCode, onPickHub, t]);

  return (
    <Card data-testid="hub-map">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-heading flex items-center gap-2">
          <MapIcon className="w-4 h-4 text-sky-600" />
          {t("hub.mapTitle")}
        </CardTitle>
        <CardDescription>{t("hub.mapHint")}</CardDescription>
      </CardHeader>
      <CardContent>
        <div ref={containerRef} className="h-[360px] w-full rounded-lg overflow-hidden border border-slate-200" />
      </CardContent>
    </Card>
  );
}
